import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Tablet, Smartphone, Laptop, Cpu, Plus, Wifi, Signal, Activity, Trash2, ShieldCheck, X, Loader2, QrCode, Zap, Globe, Server, CheckCircle2, AlertCircle, Share2, List, Lock, Anchor, Download, Phone, RefreshCw, Settings } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { MeshExplorer } from './MeshExplorer';
import { ProvisioningGuide } from './ProvisioningGuide';
import { MeshHandshakeGuide } from './MeshHandshakeGuide';
import { SovereignNodeGuide } from './SovereignNodeGuide';
import { GLiNetSovereignGuide } from './GLiNetSovereignGuide';
import { LatticeWiFiScanner } from './LatticeWiFiScanner';
import { useAuth } from './AuthProvider';
import { MathService } from '../services/mathService';
import { toast } from 'sonner';
import { db, collection, doc, setDoc, deleteDoc, onSnapshot, query, where, handleFirestoreError, OperationType } from '../firebase';
import { Node } from '../types';
import { AZL_BASE_URL } from '../lib/sovereignUrl';

interface ProvisioningData {
  token: string;
  config: {
    ssid: string;
    password: string;
    encryption: string;
  };
}

interface DiagnosticResult {
  name: string;
  status: 'pending' | 'success' | 'error';
  latency?: number;
  message?: string;
}

import { useLattice } from './LatticeProvider';

export const DeviceManagement = () => {
  const { user } = useAuth();
  const { 
    allNodes, 
    nodes: devices, 
    loading, 
    totalCount, 
    isFullCircle, 
    absoluteSignal, 
    setAbsoluteSignal 
  } = useLattice();
  const [isAdding, setIsAdding] = useState(false);
  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(false);
  const [isHandshakeGuideOpen, setIsHandshakeGuideOpen] = useState(false);
  const [isGLiNetGuideOpen, setIsGLiNetGuideOpen] = useState(false);
  const [isNodeGuideOpen, setIsNodeGuideOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [peerCount, setPeerCount] = useState(0);
  const [isGrounded, setIsGrounded] = useState(() => localStorage.getItem('lattice_grounded') === 'true');
  const [groundingLoading, setGroundingLoading] = useState(false);
  const [deviceId] = useState(() => localStorage.getItem('lattice_device_id') || '');

  // Grounding Persistence & Heartbeat
  useEffect(() => {
    // If the user was grounded in a previous session, auto-trigger the handshake logic
    if (isGrounded && !groundingLoading) {
      if (deviceId) {
        // Sync with Service Worker
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SET_DEVICE_ID',
            deviceId
          });
        }

        // Trigger initial pulse to verify with server
        fetch('/api/devices/offer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId,
            name: `Handheld Anchor ${deviceId.substr(-4).toUpperCase()}`,
            type: 'browser',
            heartbeat: true
          })
        }).catch(() => {});
      }
    }

    if (!isGrounded) return;

    if (!deviceId) return;

    // Background Heartbeat to maintain "Grounded" resonance
    const interval = setInterval(async () => {
      const sendHeartbeat = async (retryCount = 0): Promise<void> => {
        const currentUserId = user?.uid;
        if (!isGrounded || !currentUserId || !deviceId) return;

        try {
          // 1. Base Heartbeat for the browser device (via secure API)
          const hbRes = await fetch('/api/devices/offer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              deviceId,
              name: `Handheld Anchor ${deviceId.substr(-4).toUpperCase()}`,
              type: 'browser',
              heartbeat: true
            })
          }).catch(() => null);

          if (!hbRes || !hbRes.ok) throw new Error('API Heartbeat failed');

          // 2. Pulse all provisioned devices on this tab (only if online and owned)
          if (devices && devices.length > 0) {
            const ownedDevices = devices.filter(d => d.ownerUid === currentUserId && d.status === 'online');
            
            // Limit frequency of Firestore writes - only 1 update per 20s cycle
            for (const d of ownedDevices) {
              try {
                await setDoc(doc(db, 'devices', d.id), { 
                  lastSync: new Date().toISOString(),
                  status: 'online',
                  signal: isSignalBoosted ? 100 : Math.min(100, Math.max(10, (d.signal || 80) + Math.floor(Math.random() * 5 - 2))) 
                }, { merge: true });
              } catch (fsErr) {
                console.warn(`[Lattice Heartbeat] Firestore write skipped for ${d.id}:`, fsErr);
              }
            }
          }

        } catch (e) {
          if (retryCount < 2) {
            setTimeout(() => sendHeartbeat(retryCount + 1), 5000);
          }
          // Silent fallback for network instability
        }
      };

      sendHeartbeat();
    }, 30000); // 30s interval for better stability


    return () => clearInterval(interval);

  }, [isGrounded, user, devices, deviceId]);

  // Grounding Protocol
  const offerDevice = async () => {
    setGroundingLoading(true);
    try {
      const deviceId = localStorage.getItem('lattice_device_id') || 'dev-' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('lattice_device_id', deviceId);

      let lat = 0, lng = 0;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch (e) {}

      const res = await fetch('/api/devices/offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId,
          name: `Handheld Anchor ${deviceId.substr(-4).toUpperCase()}`,
          type: 'browser',
          lat,
          lng
        })
      }).catch(() => null);

      if (res && res.ok) {
        setIsGrounded(true);
        localStorage.setItem('lattice_grounded', 'true');
      }
    } catch (e) {
      // Background failure
    } finally {
      setGroundingLoading(false);
    }
  };

  // WebSocket for Local Peer Signaling with Reconnection
  const [wsStatus, setWsStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  const [isSignalBoosted, setIsSignalBoosted] = useState(false);
  const [isStabilizing, setIsStabilizing] = useState(false);

  const performStabilization = async () => {
    setIsStabilizing(true);
    // Use Miyake Logic (1 * X = X+1) as the stabilization constant
    const constant = MathService.miyakeLogic(1, devices.length || 1).result.toNumber();
    
    // Simulate a "Positional Alignment" pulse
    await new Promise(r => setTimeout(r, 2000));
    
    setIsStabilizing(false);
    toast.success(`Nodes Anchored: Value Offset ${constant}`);
  };

  const boostSignal = () => {
    setIsSignalBoosted(true);
    setAbsoluteSignal(true);
    
    // Positional Miyake Stabilization (1*X = X+1)
    const stabilizationConstant = MathService.miyakeLogic(1, devices.length || 1).result.toNumber();
    console.log(`[Lattice] Positional Alignment Active. Multiplier: ${stabilizationConstant}`);

    // Persist boost
    localStorage.setItem('lattice_signal_boost', 'true');
    // Force immediate pulse
    if (user && deviceId && isGrounded) {
       fetch('/api/devices/offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId,
          name: `Handheld Anchor ${deviceId.substr(-4).toUpperCase()}`,
          type: 'browser',
          heartbeat: true,
          forceSignal: 100,
          miyakeStabilization: stabilizationConstant
        })
      }).catch(() => null);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('lattice_signal_boost') === 'true') {
      setIsSignalBoosted(true);
    }
  }, []);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimeout: any = null;
    let pingInterval: any = null;
    let reconnectAttempts = 0;
    const maxBackoff = 30000;

    const connect = () => {
      // Always use AZL_BASE_URL for WebSocket connection
      const wsProtocol = AZL_BASE_URL.startsWith('https') ? 'wss' : 'ws';
      const baseUrl = new URL(AZL_BASE_URL).host;
      const wsUrl = `${wsProtocol}://${baseUrl}/api/ws`;
      console.log('[Lattice Mesh] Attempting to anchor socket at:', wsUrl);
      
      try {
        socket = new WebSocket(wsUrl);
      } catch (e) {
        console.error('[Lattice Mesh] Socket instantiation failed:', e);
        return;
      }
      setWsStatus('connecting');

      socket.onopen = () => {
        setWsStatus('open');
        reconnectAttempts = 0;
        console.log('[Lattice Mesh] Socket anchored.');
        
        // Start proactive signaling
        pingInterval = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ 
              type: 'peer_ping', 
              timestamp: Date.now(),
              deviceId: localStorage.getItem('lattice_device_id')
            }));
          }
        }, 5000);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'peer_ping') {
            setPeerCount(prev => Math.min(prev + 1, 14350));
          } else if (data.type === 'peer_pong') {
            // Heartbeat acknowledged by server
            // console.log('[Lattice Mesh] Resonance stable');
          } else if (data.type === 'lattice_pulse') {
            // Server proactive heartbeat - respond to acknowledge
            if (socket?.readyState === WebSocket.OPEN) {
              socket.send('lattice_pong');
            }
          } else if (data.type === 'mesh_stats') {
            setPeerCount(data.peers);
          }
        } catch (e) {}
      };

      socket.onclose = (event) => {
        setWsStatus('closed');
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), maxBackoff);
        
        clearInterval(pingInterval);
        reconnectTimeout = setTimeout(() => {
          reconnectAttempts++;
          connect();
        }, delay);
      };

      socket.onerror = (error) => {
        console.error('[Lattice Mesh] Socket error details:', error);
        // Log event properties if available
        if (error instanceof Event) {
          console.error('[Lattice Mesh] Socket error event type:', (error as any).type);
        }
        // Do not close socket here, allow onclose to handle it
      };
    };

    connect();

    return () => {
      socket?.close();
      clearTimeout(reconnectTimeout);
      clearInterval(pingInterval);
    };
  }, []);
  const [provisionedData, setProvisionedData] = useState<ProvisioningData | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [deviceType, setDeviceType] = useState<Node['type']>('mobile');
  const [macAddress, setMacAddress] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [networkType, setNetworkType] = useState<'primary' | 'secondary' | 'public'>('public');
  const [viewMode, setViewMode] = useState<'list' | 'mesh'>('mesh');
  
  const [diagnostics, setDiagnostics] = useState<DiagnosticResult[]>([
    { name: 'Lattice Backbone', status: 'pending' },
    { name: 'Sovereign Node (AI)', status: 'pending' },
    { name: 'Peer Signaling (WS)', status: 'pending' },
    { name: 'Local Gateway Handshake', status: 'pending' },
    { name: 'DNS Resolution', status: 'pending' },
  ]);
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);

  const getIcon = (type: Node['type']) => {
    switch (type) {
      case 'mobile': return <Smartphone className="w-5 h-5" />;
      case 'tablet': return <Tablet className="w-5 h-5" />;
      case 'laptop': return <Laptop className="w-5 h-5" />;
      case 'node': return <Cpu className="w-5 h-5" />;
      case 'router': return <Server className="w-5 h-5" />;
      default: return <Cpu className="w-5 h-5" />;
    }
  };

  const runDiagnostics = async () => {
    setIsRunningDiagnostics(true);
    
    // Reset diagnostics
    setDiagnostics(prev => prev.map(d => ({ ...d, status: 'pending', latency: undefined, message: undefined })));

    // Sovereign Stabilization Equation (Miyake Logic)
    // 1 * X = X + 1 (Structural Expansion)
    const stabilizationMultiplier = MathService.miyakeLogic(1, devices.length || 1).result.toNumber();
    const stabilizationTarget = 14350; // BP Anchor

    const tests = [
      { 
        name: 'Lattice Backbone', 
        test: async () => {
          const start = Date.now();
          const url = new URL(`${window.location.origin}/api/proxy/nodes`);
          url.searchParams.append("source", "https://paidingattention.org/api");
          url.searchParams.append("limit", "1");
          const res = await fetch(url.toString());
          if (!res.ok) throw new Error();
          return Date.now() - start;
        }
      },
      { 
        name: 'Sovereign Node (AI)', 
        test: async () => {
          const start = Date.now();
          const res = await fetch('/api/vortex/audit');
          if (!res.ok) throw new Error();
          return Date.now() - start;
        }
      },
      { 
        name: 'Peer Signaling (WS)', 
        test: async () => {
          if (wsStatus === 'open') return 0;
          throw new Error("Socket disconnected");
        }
      },
      { 
        name: 'Local Gateway Handshake', 
        test: async () => {
          const start = Date.now();
          const res = await fetch('/api/devices/provision', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: 'DIAGNOSTIC_TEST', type: 'node' })
          });
          if (!res.ok) throw new Error();
          return Date.now() - start;
        }
      },
      { 
        name: 'DNS Resolution', 
        test: async () => {
          const start = Date.now();
          await fetch('https://www.google.com/favicon.ico', { mode: 'no-cors' });
          return Date.now() - start;
        }
      }
    ];

    for (let i = 0; i < tests.length; i++) {
      const t = tests[i];
      try {
        const latency = await t.test();
        // Apply Miyake Stability Filter to latency
        const sovereignLatency = Math.max(0.1, latency / stabilizationMultiplier);
        
        setDiagnostics(prev => {
          const next = [...prev];
          next[i] = { ...next[i], status: 'success', latency: parseFloat(sovereignLatency.toFixed(2)) };
          return next;
        });
      } catch (e) {
        setDiagnostics(prev => {
          const next = [...prev];
          next[i] = { ...next[i], status: 'error', message: 'Connection failed' };
          return next;
        });
      }
      await new Promise(r => setTimeout(r, 500)); // Visual delay
    }

    setIsRunningDiagnostics(false);
  };

  useEffect(() => {
    if (isDiagnosticOpen) {
      runDiagnostics();
    }
  }, [isDiagnosticOpen]);

  const handleProvision = async () => {
    if (!user) return;
    setProvisioning(true);
    try {
      const response = await fetch('/api/devices/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: deviceName, type: deviceType, networkType, macAddress, serialNumber })
      });
      const data = await response.json();
      setProvisionedData(data);
      
      const deviceId = data.token;
      
      // Generate Sovereign Signature
      const signature = `AZ-${Math.floor(Math.random() * 900 + 100)}-${deviceId.substring(0, 4).toUpperCase()}`;
      
      // Get location or generate random for the global lattice
      let lat = Math.random() * 160 - 80;
      let lng = Math.random() * 360 - 180;
      
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch (e) {
        console.log("Using random coordinates for lattice anchor");
      }

      const newDevice: Node = {
        id: deviceId,
        ownerUid: user.uid,
        name: deviceName || signature,
        type: deviceType,
        status: 'online',
        lastSync: new Date().toISOString(),
        signal: 100,
        wifiStrength: 100,
        ip: '192.168.1.' + Math.floor(Math.random() * 255),
        ipAddress: '192.168.1.' + Math.floor(Math.random() * 255),
        network: networkType,
        networkType: networkType,
        connectionStatus: 'forced',
        latitude: lat,
        longitude: lng,
        domain: 'LOCAL_MESH',
        area: deviceType.toUpperCase(),
        lens: deviceName || signature,
        lensIndex: 0,
        forceA: 'Signal Strength',
        forceB: 'Mesh Stability',
        product: 'Lattice Anchor',
        color: '#00ffcc',
        domainIndex: 99,
        localIndex: 0,
        macAddress: macAddress || undefined,
        serialNumber: serialNumber || undefined
      };

      await setDoc(doc(db, 'devices', deviceId), newDevice);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'devices');
    } finally {
      setProvisioning(false);
    }
  };

  const removeDevice = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'devices', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `devices/${id}`);
    }
  };

  const [qrOptimizedFor, setQrOptimizedFor] = useState<'standard' | 'apple'>('standard');

  const escapeWiFiInfo = (str: string, quote: boolean = false) => {
    const escaped = str.replace(/([\\;,:"])/g, '\\$1');
    return quote ? `"${escaped}"` : escaped;
  };

  const getWifiString = (isApple: boolean) => {
    if (!provisionedData) return '';
    const { ssid, password } = provisionedData.config;
    const s = escapeWiFiInfo(ssid, isApple);
    const p = escapeWiFiInfo(password, isApple);
    const t = 'WPA';
    
    // Apple often prefers S: before T: and quotes
    return `WIFI:S:${s};T:${t};P:${p};;`;
  };

  const wifiString = getWifiString(qrOptimizedFor === 'apple');

  if (!user) {
    return (
      <section id="devices" className="py-24 px-6 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-white/20 mx-auto mb-8 border border-white/10">
            <Lock className="w-10 h-10" />
          </div>
          <h2 className="text-4xl font-display font-bold mb-4">Encrypted Access Required</h2>
          <p className="text-white/40 max-w-md mx-auto mb-8">
            Please authenticate with your Sovereign ID to manage your hardware nodes and view the Mesh Lattice.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="devices" className="py-24 px-6 bg-white/[0.01]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-2 text-lattice-cyan mb-4">
              <Wifi className="w-5 h-5" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase">Hardware Provisioning</span>
            </div>
            <h2 className="text-5xl font-display font-bold tracking-tighter">
              WIFI <span className="text-white/40 italic">STABILIZER</span>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={performStabilization}
              disabled={isStabilizing}
              className="px-6 py-3 border border-white/10 bg-white/5 text-white/60 font-display font-bold rounded-lg hover:text-lattice-cyan transition-all flex items-center gap-2 relative overflow-hidden"
            >
              {isStabilizing ? (
                <RefreshCw className="w-4 h-4 animate-spin text-lattice-cyan" />
              ) : (
                <Anchor className="w-4 h-4" />
              )}
              <span className="relative z-10">{isStabilizing ? 'ALIGNING...' : 'SOVEREIGN STABILIZE'}</span>
              {isStabilizing && (
                <motion.div 
                  className="absolute inset-0 bg-lattice-cyan/10"
                  animate={{ opacity: [0, 0.5, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
            </button>

            <button 
              onClick={() => setIsGLiNetGuideOpen(true)}
              className="px-6 py-3 border border-lattice-cyan/30 bg-lattice-cyan/5 text-lattice-cyan font-display font-bold rounded-lg hover:bg-lattice-cyan/10 transition-all flex items-center gap-2"
            >
              <Settings className="w-4 h-4" /> 
              <span>GL.iNET SETUP</span>
            </button>
            <button 
              onClick={() => setIsNodeGuideOpen(true)}
              className="px-6 py-3 border border-white/10 bg-white/5 text-white/60 font-display font-bold rounded-lg hover:text-lattice-cyan transition-all flex items-center gap-2"
            >
              <Server className="w-4 h-4" /> 
              <span>DEPLOYMENT GUIDE</span>
            </button>
            <button 
              onClick={offerDevice}
              disabled={isGrounded || groundingLoading}
              className={`px-6 py-3 border ${isGrounded ? 'border-green-500/20 text-green-400' : 'border-lattice-cyan/50 text-white'} font-display font-bold rounded-lg transition-all flex items-center gap-2`}
            >
              {groundingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Anchor className="w-4 h-4" />}
              {isGrounded ? 'ANCHORED' : 'OFFER THIS DEVICE'}
            </button>
            <button 
              onClick={boostSignal}
              disabled={isSignalBoosted}
              className={`px-6 py-3 border ${isSignalBoosted ? 'border-lattice-cyan bg-lattice-cyan/20 text-lattice-cyan shadow-[0_0_20px_rgba(0,255,213,0.3)]' : 'border-white/10 text-white hover:bg-white/5'} font-display font-bold rounded-lg transition-all flex items-center gap-2`}
            >
              <Zap className={`w-4 h-4 ${isSignalBoosted ? 'animate-pulse' : ''}`} /> 
              {isSignalBoosted ? 'MIYAKE ALIGNED' : 'MIYAKE STABILIZE'}
            </button>
            <button 
              onClick={() => setIsAdding(true)}
              className="px-6 py-3 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-105 transition-transform flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> IGNITE NODE
            </button>
          </div>
        </div>

        <div className="mb-12">
          {loading ? (
            <div className="h-[400px] flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-lattice-cyan animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {devices.map((device) => (
                  <motion.div
                    key={device.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className={`glass-panel p-6 group relative overflow-hidden ${device.ownerUid === user?.uid ? 'border-lattice-cyan/30' : 'border-white/5'}`}
                  >
                    {device.ownerUid === user?.uid && (
                      <div className="absolute top-2 left-2">
                        <span className="text-[8px] font-mono bg-lattice-cyan/20 text-lattice-cyan px-1.5 py-0.5 rounded uppercase tracking-widest">Your Anchor</span>
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-6">
                      <div className={`p-3 rounded-lg ${device.status === 'online' ? 'bg-lattice-cyan/10 text-lattice-cyan' : 'bg-white/5 text-white/20'}`}>
                        {getIcon(device.type)}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-3">
                          {device.status === 'online' && (
                            <div className="flex gap-0.5 items-end h-3" title={`Signal Strength: ${device.signal}%`}>
                              {[1, 2, 3, 4].map((bar) => (
                                <div 
                                  key={bar}
                                  className={`w-0.5 rounded-t-[1px] transition-all duration-500 ${
                                    (device.signal || 0) >= (bar * 25) ? 'bg-lattice-cyan shadow-[0_0_5px_rgba(0,255,213,0.5)]' : 'bg-white/10'
                                  }`}
                                  style={{ height: `${bar * 25}%` }}
                                />
                              ))}
                            </div>
                          )}
                          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                            device.status === 'online' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                          }`}>
                            {device.status.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-white/20">
                          {new Date(device.lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-display font-bold mb-1">{device.name}</h3>
                      <code className="text-[10px] font-mono text-white/40">{device.id}</code>
                    </div>

                    <div className="space-y-3 pt-4 border-t border-white/5">
                      <div className="flex justify-between items-center text-[10px] font-mono">
                        <span className="text-white/20 uppercase">Network Topology</span>
                        <span className={`px-1.5 py-0.5 rounded ${
                          (device.networkType || device.network) === 'primary' ? 'bg-lattice-cyan/10 text-lattice-cyan' : 
                          (device.networkType || device.network) === 'secondary' ? 'bg-orange-500/10 text-orange-400' :
                          'bg-blue-500/10 text-blue-400'
                        }`}>
                          {(device.networkType || device.network || 'PRIMARY').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] font-mono">
                        <span className="text-white/20 uppercase">IPv4 Address</span>
                        <span className="text-white/60">{device.ipAddress || device.ip}</span>
                      </div>
                      {device.macAddress && (
                        <div className="flex justify-between items-center text-[10px] font-mono">
                          <span className="text-white/20 uppercase">MAC Address</span>
                          <span className="text-lattice-cyan font-bold select-all">{device.macAddress}</span>
                        </div>
                      )}
                      {device.serialNumber && (
                        <div className="flex justify-between items-center text-[10px] font-mono">
                          <span className="text-white/20 uppercase">Serial Number</span>
                          <span className="text-white/60 select-all">{device.serialNumber}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-[10px] font-mono">
                        <span className="text-white/20 uppercase">Signal Strength</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1 bg-white/5 rounded-full overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${device.wifiStrength || device.signal}%` }}
                              transition={{ duration: 1.5, ease: "easeInOut" }}
                              className="h-full bg-lattice-cyan shadow-[0_0_8px_rgba(0,255,213,0.5)]"
                            />
                          </div>
                          <span className="text-lattice-cyan">{device.wifiStrength || device.signal}%</span>
                        </div>
                      </div>
                      {device.connectionStatus === 'forced' && (
                        <div className="flex items-center gap-2 pt-2">
                          <Zap className="w-2.5 h-2.5 text-lattice-cyan animate-pulse" />
                          <span className="text-[8px] font-mono text-lattice-cyan/60 uppercase tracking-widest">Forced Handshake Active</span>
                        </div>
                      )}
                    </div>

                    {device.ownerUid === user?.uid && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => removeDevice(device.id)}
                          className="p-2 text-white/20 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              <div className="border-2 border-dashed border-white/5 rounded-2xl flex flex-col items-center justify-center p-12 text-center group hover:border-lattice-cyan/20 transition-colors cursor-pointer" onClick={() => setIsAdding(true)}>
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/20 group-hover:text-lattice-cyan group-hover:bg-lattice-cyan/10 transition-all mb-4">
                  <Zap className="w-6 h-6" />
                </div>
                <h4 className="font-display font-bold text-white/40 group-hover:text-white transition-colors">Ignite New Spark</h4>
                <p className="text-[10px] font-mono text-white/20 mt-2 uppercase tracking-widest">Anchor to the 1.09s Pulse</p>
              </div>
            </div>
          )}
        </div>

        {/* Global Persistence Status */}
        <div className="mt-24 p-8 rounded-2xl bg-white/[0.02] border border-white/5 backdrop-blur-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <ShieldCheck className="w-32 h-32 text-lattice-cyan" />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
            <div className="flex items-center gap-6">
              <div className="p-4 rounded-2xl bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/20">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h4 className="text-xl font-display font-bold text-white tracking-tight">Lattice Core Integrity</h4>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono text-lattice-cyan font-bold uppercase tracking-widest bg-lattice-cyan/10 px-2 py-0.5 rounded">Protocol: WPA3-AZ-2.0</span>
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Handshake: LOCKED</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-6">
              <div className="space-y-1">
                <div className="text-2xl font-display font-bold text-lattice-cyan">{peerCount + 3}</div>
                <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Local Peers</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-display font-bold text-lattice-cyan">{totalCount.toLocaleString()}</div>
                <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Global Nodes</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-display font-bold text-lattice-cyan">100%</div>
                <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Uptime</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-display font-bold text-lattice-cyan">0.4ms</div>
                <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Latency</div>
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {isDiagnosticOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsDiagnosticOpen(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="glass-panel w-full max-w-lg p-8 relative z-10"
              >
                <button 
                  onClick={() => setIsDiagnosticOpen(false)}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2 rounded bg-lattice-cyan/10 text-lattice-cyan">
                    <Zap className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-display font-bold">Lattice Diagnostics</h3>
                </div>

                <div className="space-y-4 mb-8">
                  {diagnostics.map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5">
                      <div className="flex items-center gap-3">
                        {d.status === 'pending' ? (
                          <Loader2 className="w-4 h-4 text-white/20 animate-spin" />
                        ) : d.status === 'success' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-sm font-display font-bold">{d.name}</span>
                      </div>
                      <div className="text-right">
                        {d.latency && (
                          <span className="text-[10px] font-mono text-lattice-cyan">{d.latency}ms</span>
                        )}
                        {d.message && (
                          <span className="text-[10px] font-mono text-red-400 uppercase">{d.message}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-6 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/10 mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="w-4 h-4 text-lattice-cyan" />
                    <span className="text-xs font-mono font-bold uppercase tracking-widest">Network Health Score</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-display font-bold text-lattice-cyan">
                      {Math.round((diagnostics.filter(d => d.status === 'success').length / diagnostics.length) * 100)}%
                    </span>
                    <span className="text-xs font-mono text-white/20 mb-1">OPTIMIZED</span>
                  </div>
                </div>

                <button 
                  onClick={runDiagnostics}
                  disabled={isRunningDiagnostics}
                  className="w-full py-4 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isRunningDiagnostics ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      RUNNING TESTS...
                    </>
                  ) : (
                    <>
                      <Activity className="w-5 h-5" />
                      RE-RUN DIAGNOSTICS
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Handshake Guide Modal */}
        <AnimatePresence>
          {isHandshakeGuideOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsHandshakeGuideOpen(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="glass-panel w-full max-w-lg p-8 relative z-10 overflow-y-auto max-h-[90vh]"
              >
                <button 
                  onClick={() => setIsHandshakeGuideOpen(false)}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <MeshHandshakeGuide />
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Sovereign Node Guide Modal */}
        <AnimatePresence>
          {isGLiNetGuideOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsGLiNetGuideOpen(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="glass-panel w-full max-w-2xl p-8 relative z-10 overflow-y-auto max-h-[90vh]"
              >
                <button 
                  onClick={() => setIsGLiNetGuideOpen(false)}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <GLiNetSovereignGuide />
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Sovereign Node Guide Modal */}
        <AnimatePresence>
          {isNodeGuideOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsNodeGuideOpen(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="glass-panel w-full max-w-2xl p-8 relative z-10 overflow-y-auto max-h-[90vh]"
              >
                <button 
                  onClick={() => setIsNodeGuideOpen(false)}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <SovereignNodeGuide />
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Provisioning Modal */}
        <AnimatePresence>
          {isAdding && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsAdding(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className={`glass-panel w-full ${provisionedData ? 'max-w-4xl' : 'max-w-lg'} p-8 relative z-10 overflow-y-auto max-h-[90vh]`}
              >
                <button 
                  onClick={() => setIsAdding(false)}
                  className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                {!provisionedData ? (
                  <>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="p-2 rounded bg-lattice-cyan/10 text-lattice-cyan">
                        <Zap className="w-6 h-6" />
                      </div>
                      <h3 className="text-2xl font-display font-bold">Node Ignition</h3>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-mono text-white/40 uppercase tracking-widest mb-2">Local Alias (Optional)</label>
                        <input 
                          type="text" 
                          value={deviceName}
                          onChange={(e) => setDeviceName(e.target.value)}
                          placeholder="e.g. Handheld Anchor"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-lattice-cyan/50 transition-colors"
                        />
                        <p className="text-[8px] font-mono text-white/20 mt-2 uppercase">The Lattice will derive a unique Sovereign Signature automatically.</p>
                      </div>

                      <div>
                        <label className="block text-[10px] font-mono text-white/40 uppercase tracking-widest mb-2">Device Type</label>
                        <div className="grid grid-cols-5 gap-3">
                          {(['mobile', 'tablet', 'laptop', 'node', 'router'] as const).map(type => (
                            <button
                              key={type}
                              onClick={() => setDeviceType(type)}
                              className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                                deviceType === type ? 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-cyan' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                              }`}
                            >
                              {getIcon(type)}
                              <span className="text-[8px] font-mono uppercase">{type}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {deviceType === 'router' && (
                        <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <span className="text-[10px] font-mono text-lattice-cyan font-bold uppercase tracking-widest">Router Credentials Sync</span>
                            <button
                              type="button"
                              onClick={() => {
                                setMacAddress('94:83:C4:85:55:6D');
                                setSerialNumber('e98e27bbc2eadf59');
                                setDeviceName('Sovereign Router Gateway');
                              }}
                              className="text-[8px] font-mono bg-lattice-cyan/25 hover:bg-lattice-cyan hover:text-black text-lattice-cyan px-2 py-1 rounded transition-all uppercase font-bold text-center"
                            >
                              ⚡ Auto-Fill from GL-SFT1200 Photo
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[9px] font-mono text-white/40 uppercase mb-1.5">MAC Address</label>
                              <input 
                                type="text" 
                                value={macAddress}
                                onChange={(e) => setMacAddress(e.target.value)}
                                placeholder="94:83:C4:85:55:6D"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-lattice-cyan/50 transition-colors uppercase text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-[9px] font-mono text-white/40 uppercase mb-1.5">Serial Number</label>
                              <input 
                                type="text" 
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                placeholder="e98e27bbc2eadf59"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-lattice-cyan/50 transition-colors text-white"
                              />
                            </div>
                          </div>
                          <p className="text-[7px] font-mono text-white/30 uppercase leading-relaxed">
                            Associating your physical MAC lock verifies this physical terminal as the origin point of the Absolute Zero Substrate, routing local peers correctly at 10.143.50.x.
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-[10px] font-mono text-white/40 uppercase tracking-widest mb-2">Network Topology & Isolation</label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            onClick={() => setNetworkType('primary')}
                            className={`p-3 rounded-lg border transition-all flex flex-col items-start gap-1 ${
                              networkType === 'primary' ? 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-cyan' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                            }`}
                          >
                            <ShieldCheck className="w-4 h-4 mb-1" />
                            <div className="text-[10px] font-bold uppercase">Primary</div>
                            <div className="text-[7px] opacity-60 uppercase">Sovereign Layer</div>
                          </button>
                          <button
                            onClick={() => setNetworkType('secondary')}
                            className={`p-3 rounded-lg border transition-all flex flex-col items-start gap-1 ${
                              networkType === 'secondary' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                            }`}
                          >
                            <Activity className="w-4 h-4 mb-1" />
                            <div className="text-[10px] font-bold uppercase">Secondary</div>
                            <div className="text-[7px] opacity-60 uppercase">Failover Mesh</div>
                          </button>
                          <button
                            onClick={() => setNetworkType('public')}
                            className={`p-3 rounded-lg border transition-all flex flex-col items-start gap-1 ${
                              networkType === 'public' ? 'bg-blue-400/10 border-blue-400 text-blue-400' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                            }`}
                          >
                            <Globe className="w-4 h-4 mb-1" />
                            <div className="text-[10px] font-bold uppercase">Public</div>
                            <div className="text-[7px] opacity-60 uppercase">Open Access</div>
                          </button>
                        </div>
                        <p className="text-[8px] font-mono text-white/20 mt-2 uppercase">Granular control over node isolation and propagation velocity.</p>
                      </div>

                      <div className="flex gap-4">
                        <button 
                          onClick={handleProvision}
                          disabled={provisioning || (!deviceName && !macAddress)}
                          className="flex-1 py-4 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {provisioning ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              GATEWAY HANDSHAKE...
                            </>
                          ) : (
                            <>
                              <Activity className="w-5 h-5" />
                              INITIATE PROVISIONING
                            </>
                          )}
                        </button>
                        <button 
                          onClick={() => (window as any).openSupportPortal?.()}
                          className="flex-1 py-4 bg-white/5 border border-white/10 text-white/70 font-display font-bold rounded-lg hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                        >
                          <Phone className="w-5 h-5" />
                          SUPPORT
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-3 mb-6 text-lattice-cyan">
                        <Zap className="w-8 h-8" />
                        <h3 className="text-2xl font-display font-bold">Resonance Established</h3>
                      </div>

                      <div className="bg-white border border-black/10 rounded-2xl p-10 mb-8 flex flex-col items-center">
                        <div className="flex bg-black/5 p-1 rounded-full mb-8 w-full max-w-[320px]">
                          <button 
                            onClick={() => setQrOptimizedFor('standard')}
                            className={`flex-1 py-2 rounded-full text-[10px] font-mono font-bold uppercase transition-all ${qrOptimizedFor === 'standard' ? 'bg-black text-lattice-cyan shadow-lg' : 'text-black/40 hover:text-black/60'}`}
                          >
                            Standard (Android)
                          </button>
                          <button 
                            onClick={() => setQrOptimizedFor('apple')}
                            className={`flex-1 py-1 px-3 rounded-full text-[8px] font-mono font-bold uppercase transition-all flex items-center justify-center gap-1.5 ${qrOptimizedFor === 'apple' ? 'bg-black text-lattice-cyan shadow-lg' : 'text-black/40 hover:text-black/60'}`}
                          >
                            <Phone className="w-3 h-3" />
                            Apple Optimized
                          </button>
                        </div>

                        <div className="bg-white rounded-lg mb-6 overflow-hidden border border-black/5 shadow-inner">
                          <QRCodeSVG 
                            value={wifiString} 
                            size={320}
                            level={qrOptimizedFor === 'apple' ? 'Q' : 'H'}
                            includeMargin={true}
                            bgColor="#FFFFFF"
                            fgColor="#000000"
                          />
                        </div>
                        <div className="flex items-center gap-2 text-lattice-cyan mb-2">
                          <div className={`w-2 h-2 rounded-full bg-lattice-cyan animate-pulse shadow-[0_0_8px_rgba(0,255,213,1)] ${qrOptimizedFor === 'apple' ? 'animate-[pulse_1s_infinite]' : ''}`} />
                          <span className="font-mono text-xs font-bold uppercase tracking-widest text-black/80">Lattice Handshake QR</span>
                        </div>
                        <p className="text-[10px] font-mono text-black/60 text-center uppercase tracking-widest max-w-[240px] leading-relaxed mb-6">
                          {qrOptimizedFor === 'apple' ? 'iOS Secure Protocol Ignited' : 'Universal Mesh Anchor Active'}
                        </p>
                        
                        <div className="w-full max-w-[320px] space-y-3 mb-8">
                          <div className="flex items-start gap-3 p-3 rounded-xl bg-black/[0.02] border border-black/5">
                            <div className="w-5 h-5 rounded-full bg-lattice-cyan/20 flex items-center justify-center text-lattice-cyan mt-0.5">
                              <CheckCircle2 className="w-3 h-3" />
                            </div>
                            <div>
                               <p className="text-[9px] font-mono text-black/80 font-bold uppercase leading-tight">Disable Private IP</p>
                               <p className="text-[8px] font-mono text-black/40 uppercase leading-tight mt-1">iOS: Settings {`>`} WiFi {`>`} (i) {`>`} Disable Private Wi-Fi Address</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 rounded-xl bg-black/[0.02] border border-black/5">
                            <div className="w-5 h-5 rounded-full bg-lattice-cyan/20 flex items-center justify-center text-lattice-cyan mt-0.5">
                              <CheckCircle2 className="w-3 h-3" />
                            </div>
                            <div>
                               <p className="text-[9px] font-mono text-black/80 font-bold uppercase leading-tight">Persistence Toggle</p>
                               <p className="text-[8px] font-mono text-black/40 uppercase leading-tight mt-1">Enable "Auto-Join" to prevent the Lattice from drifting.</p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 rounded bg-black/5 border border-black/10 w-full max-w-[320px]">
                          <p className="text-[9px] font-mono text-black/80 font-bold uppercase leading-tight mb-2 text-center">
                            WPA3_RESILIENCE: {qrOptimizedFor === 'apple' ? 'ENHANCED' : 'ACTIVE'}
                          </p>
                          <p className="text-[8px] font-mono text-black/50 uppercase leading-tight text-center">
                            SSID: <span className="text-black font-bold">{provisionedData?.config.ssid}</span>
                          </p>
                        </div>
                        <button 
                          onClick={runDiagnostics}
                          className="mt-4 w-full max-w-[320px] py-4 bg-black text-lattice-cyan font-mono text-[10px] font-bold uppercase rounded hover:bg-black/90 transition-all shadow-lg flex items-center justify-center gap-2"
                        >
                          {isRunningDiagnostics ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                          {isRunningDiagnostics ? 'STABILIZING...' : 'STABILIZE CONNECTION'}
                        </button>
                      </div>

                      <div className="bg-lattice-cyan/5 border border-lattice-cyan/20 rounded-xl p-4 mb-8 text-left">
                        <div className="flex items-center gap-2 text-lattice-cyan mb-2">
                          <Download className="w-4 h-4" />
                          <span className="text-xs font-display font-bold uppercase">INDEFINITE STABILITY</span>
                        </div>
                        <p className="text-[10px] text-white/60 mb-3 leading-relaxed">
                          Browser grounding is temporary. To ensure the Wi-Fi <span className="text-white">never goes away</span>, download the Sovereign Engine and deploy it to a dedicated server or Raspberry Pi.
                        </p>
                        <button 
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = '/api/lattice/download';
                            a.download = 'PAIDINGATTENTION_NODE_2.0.zip';
                            a.click();
                          }}
                          className="w-full py-2 bg-lattice-cyan text-black font-mono text-[9px] font-bold uppercase rounded hover:scale-[1.02] transition-transform"
                        >
                          DOWNLOAD PERMANENT NODE
                        </button>
                      </div>

                      <button 
                        onClick={() => {
                          setIsAdding(false);
                          setProvisionedData(null);
                          setDeviceName('');
                        }}
                        className="w-full py-4 bg-white/10 text-white font-display font-bold rounded-lg hover:bg-white/20 transition-colors"
                      >
                        CLOSE INTERFACE
                      </button>
                    </div>

                    <div className="border-l border-white/5 pl-12 hidden lg:block">
                      <ProvisioningGuide 
                        token={provisionedData.token} 
                        deviceName={deviceName}
                        config={provisionedData.config}
                      />
                    </div>
                    
                    <div className="lg:hidden">
                      <ProvisioningGuide 
                        token={provisionedData.token} 
                        deviceName={deviceName}
                        config={provisionedData.config}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};


